/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import styled, { css } from 'styled-components'

import FlexBox from 'src/components/Common/FlexBox'
import Icon from 'src/components/Icon'
import { useThemeContext } from 'src/contexts/ThemeContext'
import { pxToEm, pxToVw } from 'src/functions/units'

import { useDonationIdentityCollectorContext } from '../donation-identity-collector.hooks'
import { IdentityCollectorForm } from './IdentityCollectorForm'
import { IdentityConfirmForm } from './IdentityConfirmForm'

type Props = {
  identityConfirm?: boolean
}

export const IdentityCollectorModal = ({ identityConfirm = false }: Props) => {
  const { colors } = useThemeContext()
  const { identityIsValid } = useDonationIdentityCollectorContext()

  return (
    <>
      <StyledArticle>
        <FlexBox as="header" columnGap={12} alignItems="center">
          <Icon icon="caret-right-circle" color={colors.primary600} size={26} />
          <h2>Why is my identity required?</h2>
        </FlexBox>

        <p>
          Your data will be shared with our MAS-licensed payments vendor, in line with regulatory
          requirements. For donations of SGD$200 and above, your personal data will also be shared
          with our charity beneficiary, SG Enable, for processing of tax deduction.
        </p>
      </StyledArticle>

      <StyledHorizontalLine />

      {!identityIsValid || (identityIsValid && !identityConfirm) ? (
        <IdentityCollectorForm />
      ) : (
        <IdentityConfirmForm />
      )}
    </>
  )
}

const StyledArticle = styled.article`
  h2 {
    margin: 0;
    font-size: ${pxToVw(24)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray500};
  }

  p {
    margin: 0;
    margin-top: ${pxToVw(15)};
    font-size: ${pxToVw(18)};
    line-height: ${pxToEm(24, 18)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      h2 {
        font-size: ${pxToVw(24, 720)};
      }

      p {
        margin-top: ${pxToVw(15, 720)};
        font-size: ${pxToVw(18, 720)};
      }
    `}
  `}
`

const StyledHorizontalLine = styled.hr`
  margin-block: 1.5625vw;
  border: none;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(30, 720)};
    `}
  `}
`
